import { useMemo } from "react";

import { Column, Slider } from "@bridgesplit/ui";
import { formatLeverage } from "@bridgesplit/abf-react";
import { TEST_IDS } from "app/constants";

interface LeverageSliderProps {
    multiplierWithoutDebounce: number | undefined;
    maxLeverageValue: number;
    onMultiplierWithoutDebounceChange: (multiplier: number) => void;
    onMultiplierDebouncedChange: (multiplier: number) => void;
    label?: string;
    disabled?: boolean;
}

export default function LoopLeverageSlider({
    multiplierWithoutDebounce,
    maxLeverageValue,
    onMultiplierWithoutDebounceChange,
    onMultiplierDebouncedChange,
    disabled = false
}: LeverageSliderProps) {
    const minValue = 1;
    const range = maxLeverageValue - minValue;
    const buffer = range * 0.15;

    const marks = useMemo(() => {
        if (minValue >= maxLeverageValue) return [];
        const showDecimal = maxLeverageValue % 1 !== 0;
        return [
            {
                value: minValue,
                label: formatLeverage(minValue, 0)
            },
            {
                value: maxLeverageValue,
                label: formatLeverage(maxLeverageValue, showDecimal ? 1 : 0)
            }
        ];
    }, [maxLeverageValue]);

    if (minValue >= maxLeverageValue) return null;

    const hideStartMark = multiplierWithoutDebounce ? multiplierWithoutDebounce <= minValue + buffer : true;
    const hideEndMark = multiplierWithoutDebounce ? multiplierWithoutDebounce >= maxLeverageValue - buffer : false;

    return (
        <Column>
            <Slider
                data-testid={TEST_IDS.LOOP_LEVERAGE_SLIDER}
                marks={marks}
                step={0.01}
                min={minValue}
                max={maxLeverageValue}
                value={multiplierWithoutDebounce}
                setValue={(multiplier) => {
                    onMultiplierWithoutDebounceChange(multiplier);
                    onMultiplierDebouncedChange(multiplier);
                }}
                valueLabelFormat={(value) => {
                    const showDecimal = value % 1 !== 0;
                    return formatLeverage(value, showDecimal ? 1 : 0);
                }}
                sx={{
                    "& .MuiSlider-valueLabel": {
                        background: "none",
                        color: "currentColor",
                        transform: "translateY(100%) !important",
                        bottom: "-5px"
                    },
                    "& .MuiSlider-markLabel[data-index='0']": {
                        left: "0% !important",
                        transform: "none"
                    },
                    "& .MuiSlider-markLabel[data-index='1']": {
                        left: "100% !important",
                        transform: "translateX(-100%)"
                    }
                }}
                valueLabelDisplay={"auto"}
                hideStartMark={hideStartMark}
                hideEndMark={hideEndMark}
                disabled={disabled}
            />
        </Column>
    );
}
